import React, { Dispatch, useRef, useState } from 'react';
import Head from 'next/head';
import { CurrentUser, FormNotifications, LocationData, ModalProps } from 'types';
import { sendLocationStepData } from 'api';
import { composeAddAddressError } from 'utils/formatters';
import { getSignUpSteps } from 'utils/services';
import { SIGN_UP_STEP_CAPTIONS } from 'consts';
import { GetPlainSignUpSteps } from './Steps';
import { Stepper } from 'components/Stepper';
import { Modal } from 'components/Modal';

import styles from './SignUp.module.scss';

export type SignUpStepperProps = {
  activities?: Record<string, string>;
  currentUser: CurrentUser;
  exitStepper: () => void;
  setCurrentUser: Dispatch<CurrentUser>;
  user?: CurrentUser;
};

export const SignUpStepper = ({
  activities = {},
  currentUser,
  exitStepper,
  setCurrentUser,
  user,
}: SignUpStepperProps) => {
  const [notifications, setNotifications] = useState<Record<string, FormNotifications>>({});
  const [modalProps, setModalProps] = useState<ModalProps | null>(null);
  const addressRef = useRef<any>(null);
  const { signUpSteps } = currentUser || {};
  const currentStepIndex = signUpSteps?.current || 0;
  const beforeCodeVerificationStepIndex = 2;

  const goToNextStep = (user?: CurrentUser, skipSteps?: number) => {
    if (!user) return;
    const isLastStep = SIGN_UP_STEP_CAPTIONS.length === currentStepIndex;
    user && setCurrentUser(user);
    const signUpSteps = { ...getSignUpSteps(user || {}), current: currentStepIndex + (skipSteps > 0 ? skipSteps : 0) + 1 };
    !isLastStep && setCurrentUser({ ...user, signUpSteps });
  };

  const stepBack = () => {
    if (currentStepIndex > 0) {
      const newIndex = currentStepIndex - (currentStepIndex === beforeCodeVerificationStepIndex ? 2 : 1);
      const signUpSteps = { ...getSignUpSteps(currentUser || {}), current: newIndex };
      setCurrentUser({ ...currentUser, signUpSteps });
    } else {
      exitStepper();
      return 0;
    }
  };

  const onLocationPass = async (data: LocationData) => {
    if (addressRef.current === data.location_address) {
      data.without_shipping_validation = true;
    }
    const locationResponse = await sendLocationStepData(data);
    if (locationResponse.ok && locationResponse.data) {
      goToNextStep(locationResponse.data);
      setNotifications((notifications) => {
        delete notifications.signUpStep3;
        return notifications;
      });
    } else {
      const composedErrors = composeAddAddressError(locationResponse);
      // @ts-ignore
      if (composedErrors.showWarning) {
        setModalProps({
          title: 'The address seems invalid.',
          description: "Proceed only if you're certain. Otherwise, you won't be able to sell or buy anything.",
          actionButton: {
            caption: 'Save Address',
            action: () => {
              addressRef.current = data.location_address;
              setModalProps(null);
            },
            buttonType: 'red',
          },
          cancelButton: {
            caption: 'Cancel',
            action: () => setModalProps(null),
            buttonType: 'white',
          },
        });
      } else {
        setNotifications((notifications) => {
          notifications.signUpStep3 = composedErrors;
          return { ...notifications };
        });
      }
    }
  };

  const steps = GetPlainSignUpSteps({
    user: currentUser,
    notifications,
    setNotifications,
    activities,
    onLocationPass,
    goToNextStep,
    stepBack,
  });

  return (
    <>
      {signUpSteps ? (
        <>
          <Head>
            <title>Sign Up | SENDY</title>
            <meta name="viewport" content="initial-scale=1.0, width=device-width, height=device-height" />
            <meta httpEquiv="content-type" content="text/html; charset=UTF-8" />
          </Head>
          <Stepper
            currentStep={steps[currentStepIndex]}
            contentWrapperStyles={styles.stepperContainer}
            passedSteps={signUpSteps.passed || []}
            setStep={(stepIndex) => {
              setCurrentUser({ ...user, signUpSteps: {...user?.signUpSteps, current: stepIndex} })
            }}
            steps={steps}
            stepperName="New account"
            stepBack={stepBack}
            user={user}
            title={'Sign Up | SENDY'}
          />
          {modalProps && <Modal {...modalProps} />}
        </>
      ) : null}
    </>
  );
};
